import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * @fileoverview HappeningCard — Loci atom for events in the "Within 2 hours" rail.
 *
 * 240×170 card with a category-tinted gradient cover, a breathing teal live
 * dot (top-left), a JetBrains-Mono distance (bottom-left), a Space-Grotesk
 * uppercase title, and a venue · time metadata line. Uses the `animate-loci-breathe`
 * class so the existing global `prefers-reduced-motion: reduce` rule disables the
 * live-dot animation automatically.
 */
import { forwardRef } from 'react';
import { useTheme } from '../../../theme';
import { Coord } from '../Coord';
import { SponsoredBadge } from '../SponsoredBadge';
import { SponsoredTooltip } from '../SponsoredTooltip';
import { getCategoryGradient } from './categoryGradients';
const LIVE_DOT_COLOR = '#4FC4C4';
const CARD_WIDTH = 240;
const CARD_HEIGHT = 170;
export const HappeningCard = forwardRef(({ title, venue, time, distance, category, interactive = true, featured = false, sponsoredTier, sponsoredDisclosure, onClick, className, style, 'data-testid': testId = 'happening-card', ...rest }, ref) => {
    const theme = useTheme();
    const gradient = getCategoryGradient(category);
    const isButton = interactive && typeof onClick === 'function';
    const Element = (isButton ? 'button' : 'article');
    const baseStyles = {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        flex: '0 0 auto',
        width: CARD_WIDTH,
        height: CARD_HEIGHT,
        padding: 14,
        borderRadius: 'var(--loci-radius-md, 8px)',
        overflow: 'hidden',
        background: gradient,
        color: '#FFFFFF',
        boxShadow: featured
            ? 'var(--shadow-featured, var(--loci-shadow-md, 0 3px 6px rgba(15,23,42,0.12)))'
            : 'var(--loci-shadow-sm, 0 1px 2px rgba(15,23,42,0.08))',
        transition: 'box-shadow var(--dur-standard, 220ms) var(--ease-state, ease)',
        textAlign: 'left',
        fontFamily: theme.typography.fontFamilyDisplay ?? theme.typography.fontFamily,
        // Strip button chrome when rendered as <button>
        border: featured ? '1px solid var(--clt-skyline-border, transparent)' : 'none',
        appearance: 'none',
        cursor: isButton ? 'pointer' : 'default',
        ...style,
    };
    const overlayStyles = {
        position: 'absolute',
        inset: 0,
        background: 'linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,0.35) 78%, rgba(0,0,0,0.55) 100%)',
        pointerEvents: 'none',
    };
    const liveDotStyles = {
        position: 'absolute',
        top: 12,
        left: 12,
        width: 10,
        height: 10,
        borderRadius: '9999px',
        background: LIVE_DOT_COLOR,
        boxShadow: '0 0 0 4px rgba(79, 196, 196, 0.22)',
    };
    const distanceStyles = {
        position: 'absolute',
        bottom: 12,
        left: 14,
        color: '#FFFFFF',
    };
    // WEB-36 — top-right, over the gradient cover (the card's own hero
    // media), mirroring `SponsoredBadge`'s placement guidance. The existing
    // live-dot occupies the top-left, so this never collides with it.
    const sponsoredBadgeStyles = {
        position: 'absolute',
        top: 12,
        right: 12,
        zIndex: 1,
    };
    const titleStyles = {
        position: 'relative',
        fontFamily: theme.typography.fontFamilyDisplay ?? theme.typography.fontFamily,
        fontWeight: theme.typography.fontWeight.semibold,
        fontSize: 15,
        lineHeight: 1.2,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: '#FFFFFF',
        margin: 0,
        marginBottom: 6,
        marginRight: 2,
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
    };
    const metaStyles = {
        position: 'relative',
        fontFamily: theme.typography.fontFamilyMono,
        fontSize: 11,
        letterSpacing: '0.04em',
        color: 'rgba(255, 255, 255, 0.88)',
        marginRight: 80,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontFeatureSettings: "'tnum' 1, 'zero' 1",
    };
    // WEB-535 — the "?" disclosure trigger is itself a <button>. When the
    // card renders as a <button> (or any interactive element), nesting the
    // trigger inside it would be invalid HTML (the parser closes the outer
    // button, breaking SSR hydration) and an axe `nested-interactive`
    // violation. So when disclosure copy is supplied, the badge + "?"
    // cluster renders as a positioned SIBLING of the card element inside a
    // relative wrapper, overlaid top-right — visually identical to the
    // badge-only placement, but with independent hit areas.
    const hasDisclosure = sponsoredTier !== undefined && sponsoredDisclosure !== undefined;
    const card = (_jsxs(Element, { ref: ref, className: className, style: baseStyles, onClick: isButton ? onClick : undefined, "data-testid": testId, "data-category": category ?? 'unknown', "aria-label": `${title} at ${venue}, ${time}, ${distance} away`, ...(isButton ? { type: 'button' } : {}), ...rest, children: [_jsx("span", { "aria-hidden": "true", style: overlayStyles }), sponsoredTier !== undefined && !hasDisclosure && (_jsx(SponsoredBadge, { tier: sponsoredTier, style: sponsoredBadgeStyles })), _jsx("span", { "aria-hidden": "true", className: "animate-loci-breathe", "data-testid": "happening-card-live-dot", style: liveDotStyles }), _jsx("h3", { style: titleStyles, "data-testid": "happening-card-title", children: title }), _jsxs("span", { style: metaStyles, "data-testid": "happening-card-meta", children: [venue, " \u00B7 ", time] }), _jsx(Coord, { size: "xs", color: "inherit", style: distanceStyles, "data-testid": "happening-card-distance", children: distance })] }));
    if (!hasDisclosure) {
        return card;
    }
    return (_jsxs("span", { style: {
            position: 'relative',
            display: 'inline-flex',
            flex: '0 0 auto',
        }, "data-testid": `${testId}-sponsored-wrapper`, children: [card, _jsxs("span", { style: {
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    zIndex: 1,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    // Only the "?" trigger is interactive — the badge and gap must
                    // not create a click/hover dead-zone over the card underneath.
                    // The cluster ignores pointer events; the tooltip re-enables
                    // them on itself (trigger + panel).
                    pointerEvents: 'none',
                }, "data-testid": `${testId}-sponsored-cluster`, children: [_jsx(SponsoredBadge, { tier: sponsoredTier }), _jsx(SponsoredTooltip, { tier: sponsoredTier, style: { pointerEvents: 'auto' }, children: sponsoredDisclosure })] })] }));
});
HappeningCard.displayName = 'HappeningCard';
